import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const guideLinks = [
  { title: 'Spreadsheet Guide', path: '/sugargoo-spreadsheet-guide' },
  { title: 'Shipping Cost Guide', path: '/sugargoo-shipping-cost-guide' },
  { title: 'Sugargoo Review', path: '/sugargoo-review-2026' },
  { title: 'Coupon Guide', path: '/sugargoo-coupon-discount-code' },
  { title: 'Warehouse Guide', path: '/sugargoo-warehouse-guide' },
  { title: 'Shoes Guide', path: '/best-sugargoo-shoes' },
  { title: 'Hoodies Guide', path: '/sugargoo-hoodies-sweaters-guide' },
  { title: 'Accessories Guide', path: '/sugargoo-accessories-guide' },
  { title: 'Bags Guide', path: '/sugargoo-bags-backpacks-guide' },
  { title: 'Beginner Tutorial', path: '/sugargoo-beginner-tutorial' },
];

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setIsGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-[72px]">
            <Link
              to="/"
              className="flex items-center gap-2 group"
            >
              <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-database-2-line text-lg text-white"></i>
              </div>
              <span className="font-heading text-lg md:text-xl font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                Sugargoo Sheet Help
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/"
                className="px-4 py-2 rounded-full text-sm font-label font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Home
              </Link>

              <div className="relative" ref={guideRef}>
                <button
                  onClick={() => setIsGuideOpen(!isGuideOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-label font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span>Shopping Guide</span>
                  <i
                    className={`ri-arrow-down-s-line transition-transform duration-200 ${
                      isGuideOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {isGuideOpen && (
                  <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 border border-background-200/70 rounded-xl shadow-lg overflow-hidden z-50 animate-[fadeIn_0.15s_ease-out] max-h-[420px] overflow-y-auto">
                    <div className="py-2">
                      {guideLinks.map((guide) => (
                        <Link
                          key={guide.path}
                          to={guide.path}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                          onClick={() => setIsGuideOpen(false)}
                        >
                          <div className="w-7 h-7 flex items-center justify-center bg-primary-100 rounded-md flex-shrink-0">
                            <i className="ri-file-list-line text-primary-600 text-sm"></i>
                          </div>
                          <span>{guide.title}</span>
                          <i className="ri-arrow-right-line text-foreground-400 text-xs ml-auto"></i>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200/70 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap text-sm font-label font-medium"
                >
                  <span>Categories</span>
                  <i
                    className={`ri-arrow-down-s-line transition-transform duration-200 ${
                      isDropdownOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {isDropdownOpen && (
                  <div className="absolute top-full mt-2 right-0 w-80 bg-background-50 border border-background-200/70 rounded-xl shadow-lg overflow-hidden z-50 animate-[fadeIn_0.15s_ease-out]">
                    <div className="max-h-[480px] overflow-y-auto py-2">
                      {categories.map((cat) => (
                        <a
                          key={cat.seq}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-background-200 flex-shrink-0">
                            <img
                              src={cat.imageUrl}
                              alt={cat.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="font-medium">{cat.name}</span>
                          <i className="ri-external-link-line text-foreground-400 text-xs ml-auto"></i>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary-500 text-white text-sm font-label font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            <button
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-full bg-background-100/80 border border-background-200/70 text-foreground-700 cursor-pointer"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
          <div className="absolute right-0 top-0 bottom-0 w-[85vw] max-w-sm bg-background-50 shadow-2xl flex flex-col animate-[slideIn_0.2s_ease-out]">
            <div className="flex items-center justify-between px-4 py-4 border-b border-background-200/70">
              <span className="font-heading text-lg font-semibold text-foreground-900">Menu</span>
              <button
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-2">
              <Link
                to="/"
                className="flex items-center gap-3 px-4 py-3 text-base font-label font-medium text-foreground-800 hover:bg-background-100 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg">
                  <i className="ri-home-line text-primary-600 text-lg"></i>
                </div>
                <span>Home</span>
              </Link>

              <div className="px-4 py-3">
                <p className="text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                  Shopping Guides
                </p>
                <div className="space-y-1">
                  {guideLinks.map((guide) => (
                    <Link
                      key={guide.path}
                      to={guide.path}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-md flex-shrink-0">
                        <i className="ri-file-list-line text-primary-600 text-sm"></i>
                      </div>
                      <span>{guide.title}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="px-4 py-3 border-t border-background-200/70">
                <p className="text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                  Categories
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex flex-col items-center gap-1.5 p-2 rounded-lg bg-background-100 hover:bg-background-200 transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="w-full aspect-[4/3] rounded-md overflow-hidden bg-background-200">
                        <img
                          src={cat.imageUrl}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="text-xs font-label font-medium text-foreground-700 text-center leading-tight">
                        {cat.name}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-label font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Buy It Now
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}